<script lang="ts">
	import { resolve } from '$app/paths';
	import Button from './Button.svelte';
	import MonoLabel from './MonoLabel.svelte';
	import { consent } from '$lib/content/consent';
	import { microcopy } from '$lib/content/site';
	import {
		BANNER_VERSION,
		consentChoices,
		ensureVisitorId,
		postConsent,
		readConsentCookie,
		writeConsentCookie
	} from '$lib/consent';

	let {
		variant = 'banner',
		onaccept,
		ondecline
	}: {
		variant?: 'banner' | 'inline';
		onaccept?: () => void;
		ondecline?: () => void;
	} = $props();

	let submitting = $state<string | null>(null);

	async function handleSubmit(e: SubmitEvent): Promise<void> {
		e.preventDefault();
		const submitter = e.submitter as HTMLButtonElement | null;
		submitting = submitter?.value ?? 'none';
		const { analytics, advertising } = consentChoices(submitting);

		const visitorId = ensureVisitorId(readConsentCookie());
		writeConsentCookie({
			v: 1,
			analytics,
			advertising,
			banner_version: BANNER_VERSION,
			ts: Date.now(),
			visitor_id: visitorId
		});
		await postConsent(visitorId, analytics, advertising);

		submitting = null;
		if (analytics) onaccept?.();
		else ondecline?.();
	}
</script>

<div
	class="consent {variant === 'banner' ? 'consent--banner' : ''}"
	role="region"
	aria-label={consent.label}
>
	<form method="POST" action="/consent" onsubmit={handleSubmit}>
		<MonoLabel>{consent.label}</MonoLabel>
		<p class="mt-3 text-body text-ink-500">{consent.body}</p>
		<div class="mt-5 flex flex-wrap gap-3">
			<Button
				type="submit"
				name="choice"
				value="all"
				variant="ghost"
				disabled={submitting !== null}
				loading={submitting === 'all'}
			>
				{submitting === 'all' ? microcopy.buttons.processing : consent.actions.accept}
			</Button>
			<Button
				type="submit"
				name="choice"
				value="analytics"
				variant="ghost"
				disabled={submitting !== null}
				loading={submitting === 'analytics'}
			>
				{submitting === 'analytics' ? microcopy.buttons.processing : consent.actions.analytics}
			</Button>
			<Button
				type="submit"
				name="choice"
				value="none"
				variant="ghost"
				disabled={submitting !== null}
				loading={submitting === 'none'}
			>
				{submitting === 'none' ? microcopy.buttons.processing : consent.actions.decline}
			</Button>
		</div>
		<a class="mt-3 inline-block text-sm text-ink-500 underline" href={resolve(consent.detailsHref)}
			>{consent.actions.details}</a
		>
	</form>
</div>

<style>
	.consent {
		border: 1px solid var(--color-line);
		border-radius: var(--radius-md);
		background-color: var(--color-surface);
		padding: 1.25rem 1.5rem;
	}
	.consent--banner {
		position: fixed;
		right: 1rem;
		bottom: 1rem;
		left: 1rem;
		z-index: 50;
		max-width: 26rem;
		box-shadow: var(--shadow-lift);
	}
	@media (min-width: 640px) {
		.consent--banner {
			right: auto;
			left: 1rem;
		}
	}
</style>
